import React from 'react';
import ReactDOM from 'react-dom/client';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import './index.css';
import App from './App';

// 全局设置 dayjs 为中文（Ant Design 5 的日期组件依赖 dayjs）
dayjs.locale('zh-cn');

// ⚠️ 2026-10-19 升级：chunk 加载失败自动重载兜底
//   背景：每次部署 build 会换 chunk 哈希，停留在旧标签页的用户点开懒加载页面会 404
//   （报错：Loading chunk XXX failed / ChunkLoadError）。
//
//   2026-10-16 初版的问题：
//     ① 重载后仍拿到「被缓存的旧 index.html」→ 重载也无效 → 白重载
//     ② 15s 冷却期内多次失败会被全部档掉 → 页面卡死报错
//   2026-10-19 修法：
//     ① 重载时带时间戳参数，绕过 index.html 缓存
//     ② 用 sessionStorage 标记「本次会话已重载过」，避免真死循环
//     ③ 冷却缩短到 3s，且改为「同一 URL 只试一次」
(function installChunkReloadGuard() {
  const AT_KEY = 'chunk_reload_at';
  const DONE_KEY = 'chunk_reload_done';
  const COOLDOWN = 3000;

  const isChunkError = (msg) => {
    if (!msg) return false;
    const m = String(msg);
    return /Loading chunk [\w.-]+ failed/i.test(m)
      || /Loading CSS chunk [\w.-]+ failed/i.test(m)
      || /ChunkLoadError/i.test(m)
      || /Failed to fetch dynamically imported module/i.test(m)
      || /Importing a module script failed/i.test(m);
  };

  const tryReload = (reason) => {
    try {
      // 本次会话已自动重载过一次 → 不再重载，避免真死循环（让用户手动刷）
      // 🚨 2026-10-29 修（邢先生报 Loading chunk 882 failed）：
      //   旧逻辑：DONE_KEY 一旦置 1 → 本会话永不重载 → 用户只能看报错。
      //   问题：只自动救一次往往不够——旧 index.html 还在缓存里，
      //   第一次重载加载的还是旧 index，请求的还是旧 chunk → 还是 404。
      //   新逻辑：允许重试最多 3 次，每次重载计数 +1，第 3 次后才放弃。
      const tries = Number(sessionStorage.getItem(DONE_KEY) || 0);
      if (tries >= 3) {
        console.error('[chunk-guard] 已重试 ' + tries + ' 次仍失败，请手动刷新：', reason);
        return;
      }
      const last = Number(sessionStorage.getItem(AT_KEY) || 0);
      if (Date.now() - last < COOLDOWN) return;
      sessionStorage.setItem(AT_KEY, String(Date.now()));
      sessionStorage.setItem(DONE_KEY, String(tries + 1));
    } catch (e) { /* sessionStorage 不可用时仍尝试重载 */ }

    console.warn('[chunk-guard] 检测到资源加载失败，自动重载页面：', reason);
    // ⭐ 关键：带时间戳参数，强制绕过 index.html 的浏览器缓存
    try {
      const u = new URL(window.location.href);
      u.searchParams.set('_v', String(Date.now()));
      window.location.replace(u.toString());
    } catch (e) {
      window.location.reload();
    }
  };

  // 成功加载完后清掉本次会话标记（下次部署时仍能自动重载）
  // 🚨 2026-10-29：改成 8 秒且显式置 0，兼容上面的计数逻辑
  window.addEventListener('load', () => {
    setTimeout(() => {
      try { sessionStorage.setItem(DONE_KEY, '0'); } catch (e) {}
    }, 8000);
  });

  window.addEventListener('error', (e) => {
    // 资源加载失败（script/link）或运行时错误
    const msg = (e && (e.message || (e.target && (e.target.src || e.target.href)))) || '';
    if (isChunkError(msg)) tryReload(msg);
  }, true);

  window.addEventListener('unhandledrejection', (e) => {
    const r = e && e.reason;
    const msg = (r && (r.message || r)) || '';
    if (isChunkError(msg)) tryReload(msg);
  });
})();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);
